import { ButtonHTMLAttributes, forwardRef } from 'react';
import clsx from 'clsx';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost' | 'outline' | 'warning' | 'info' | 'success';
  size?: 'sm' | 'md' | 'lg';
  isLoading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ children, variant = 'primary', size = 'md', isLoading, className, disabled, ...props }, ref) => {
    const baseStyles = 'inline-flex items-center justify-center font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-canvas disabled:opacity-50 disabled:cursor-not-allowed';

    // Semantic tokens (see DESIGN.md). Primary is the single brand-yellow
    // action; everything else stays ink-on-surface or muted semantic.
    const variantStyles = {
      primary: 'bg-accent text-on-accent hover:bg-accent-hover active:bg-accent-hover focus:ring-focus',
      secondary: 'bg-surface-2 text-ink hover:bg-surface-3 active:bg-surface-3 focus:ring-hairline-strong',
      danger: 'bg-danger text-white hover:bg-danger/90 active:bg-danger/80 focus:ring-danger',
      ghost: 'bg-transparent text-ink-muted hover:bg-surface-2 hover:text-ink active:bg-surface-3 focus:ring-hairline-strong',
      outline: 'border border-hairline text-ink hover:bg-surface-2 hover:border-hairline-strong active:bg-surface-3 focus:ring-hairline-strong',
      warning: 'bg-warning text-white hover:bg-warning/90 active:bg-warning/80 focus:ring-warning',
      info: 'bg-info text-white hover:bg-info/90 active:bg-info/80 focus:ring-info',
      success: 'bg-success text-white hover:bg-success/90 active:bg-success/80 focus:ring-success',
    };

    const sizeStyles = {
      sm: 'px-3 py-1.5 text-sm',
      md: 'px-4 py-2 text-base',
      lg: 'px-6 py-3 text-lg',
    };

    return (
      <button
        ref={ref}
        className={clsx(baseStyles, variantStyles[variant], sizeStyles[size], className)}
        disabled={disabled || isLoading}
        {...props}
      >
        {isLoading && (
          <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        )}
        {children}
      </button>
    );
  }
);

Button.displayName = 'Button';
